import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Newspaper } from "lucide-react";
import { api } from "@/lib/api";

const relativeTime = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    const d = Math.floor(diff / 86400);
    return d === 1 ? "yesterday" : `${d}d ago`;
  } catch {
    return "";
  }
};

export default function NewsPreview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/news?limit=3")
      .then((r) => mounted && setItems(r.data))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section data-testid="home-news-preview" className="container-x py-20 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <span className="eyebrow">Latest Insights</span>
          <h2 className="mt-4 h-display text-3xl sm:text-4xl">
            Banking, NBFC &amp; credit news, daily.
          </h2>
        </div>
        <Link to="/news" data-testid="home-news-all" className="btn-ghost">
          See all <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <ul className="mt-10 grid gap-5 md:grid-cols-3">
        {(loading ? Array.from({ length: 3 }).map((_, i) => ({ id: `s${i}`, _skeleton: true })) : items).map(
          (n, i) => (
            <li key={n.id}>
              {n._skeleton ? (
                <div className="card-soft p-6 h-[260px] animate-pulse" />
              ) : (
                <a
                  data-testid={`home-news-card-${i}`}
                  href={n.link}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex h-full flex-col card-soft p-6 transition-all hover:-translate-y-1 hover:border-brand-terracotta/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="rounded-full border border-brand-sand bg-brand-sand/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-forest/70">
                      {n.source}
                    </span>
                    <ArrowUpRight className="h-5 w-5 shrink-0 text-brand-terracotta opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </div>
                  <h3 className="mt-4 font-outfit font-bold text-base text-brand-forest leading-snug line-clamp-3 group-hover:text-brand-terracotta transition-colors">
                    {n.title}
                  </h3>
                  {n.summary && (
                    <p className="mt-3 text-sm text-brand-sage leading-relaxed line-clamp-3">
                      <Sparkles className="inline h-3.5 w-3.5 text-brand-terracotta mr-1 -translate-y-0.5" />
                      {n.summary}
                    </p>
                  )}
                  <div className="mt-auto pt-4 text-xs text-brand-sage">
                    {relativeTime(n.published_at)}
                  </div>
                </a>
              )}
            </li>
          )
        )}
      </ul>
    </section>
  );
}
